// Super-admin view. Two sections: paginated Users (powered by
// better-auth's admin plugin) and Invitations (our own table).
//
// Gated by session.user.role === "admin". Non-admins get redirected
// to / on mount. The server-side admin endpoints also enforce the
// check; this is just UI defense in depth.

import { Menu } from "@base-ui-components/react/menu";
import { Popover } from "@base-ui-components/react/popover";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Ban,
	Check,
	ChevronDown,
	Loader2,
	LogIn,
	MoreHorizontal,
	Plus,
	Shield,
	ShieldOff,
	Trash2,
	UserCheck,
	X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { GlassPanel, Pill, Surface } from "@/components/glass";
import { toast } from "@/components/toast";
import { authClient, useSession } from "@/lib/auth-client";

const PAGE_SIZE = 25;

interface AdminUser {
	id: string;
	name: string | null;
	email: string;
	role: string | null;
	banned: boolean | null;
	banReason: string | null;
	banExpires: number | null;
	image: string | null;
	createdAt: string;
}

interface Invitation {
	id: string;
	email: string;
	status: string;
	inviterName: string | null;
	inviterEmail: string | null;
	acceptedUserId: string | null;
	acceptedAt: string | null;
	createdAt: string;
}

export default function AdminPage() {
	const session = useSession();
	const role = session.data?.user?.role;
	const isAdmin = role === "admin";

	const [tab, setTab] = useState<"users" | "invites" | "whop">("users");

	if (session.isPending) return null;
	if (!session.data?.user) return <Navigate to="/login" replace />;
	if (!isAdmin) {
		return (
			<Surface className="flex items-center justify-center min-h-screen p-4">
				<GlassPanel className="p-6 text-center space-y-2 max-w-sm">
					<div className="text-[14px] font-medium">Admin only</div>
					<div className="text-[12px] text-[var(--color-text-muted)]">
						You need the admin role to view this page.
					</div>
				</GlassPanel>
			</Surface>
		);
	}

	return (
		<div className="h-full overflow-y-auto pt-1 pb-8">
			<div className="max-w-[980px] mx-auto px-4 space-y-4">
				<div className="flex items-center justify-between mt-2">
					<h1 className="text-[18px] font-medium tracking-tight flex items-center gap-2">
						<Shield className="w-4 h-4 text-[var(--color-text-muted)]" />
						Admin
					</h1>
					<div className="flex items-center gap-1">
						<Pill
							onClick={() => setTab("users")}
							className={
								tab === "users"
									? "ring-1 ring-white/15"
									: "opacity-70 hover:opacity-100"
							}
						>
							Users
						</Pill>
						<Pill
							onClick={() => setTab("invites")}
							className={
								tab === "invites"
									? "ring-1 ring-white/15"
									: "opacity-70 hover:opacity-100"
							}
						>
							Invitations
						</Pill>
						<Pill
							onClick={() => setTab("whop")}
							className={
								tab === "whop"
									? "ring-1 ring-white/15"
									: "opacity-70 hover:opacity-100"
							}
						>
							Whop
						</Pill>
					</div>
				</div>

				{tab === "users" ? (
					<UsersSection />
				) : tab === "invites" ? (
					<InvitationsSection />
				) : (
					<WhopEventsPanel />
				)}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Users section (powered by better-auth's admin plugin)

function UsersSection() {
	const [offset, setOffset] = useState(0);

	const usersQ = useQuery({
		queryKey: ["admin", "users", offset],
		queryFn: async () => {
			const r = await authClient.admin.listUsers({
				query: { limit: PAGE_SIZE, offset },
			});
			if (r.error) throw new Error(r.error.message ?? "list-users failed");
			return r.data as {
				users: AdminUser[];
				total: number;
				limit: number;
				offset: number;
			};
		},
	});

	// One fetch for the whole user-plan map. Cheap (no pagination, no
	// payloads) and gives every user row in this paginated view the plan
	// info it needs to render a chip and trigger an override.
	const plansQ = useQuery({
		queryKey: ["admin", "plans"],
		queryFn: async (): Promise<PlanRow[]> => {
			const r = await fetch("/api/admin/plans", { credentials: "include" });
			if (!r.ok) throw new Error(`plans: ${r.status}`);
			return ((await r.json()) as { rows: PlanRow[] }).rows;
		},
	});

	const planByUserId = new Map<string, PlanRow>();
	for (const r of plansQ.data ?? []) planByUserId.set(r.userId, r);

	const total = usersQ.data?.total ?? 0;
	const users = usersQ.data?.users ?? [];
	const page = Math.floor(offset / PAGE_SIZE) + 1;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	return (
		<GlassPanel className="p-4 space-y-3">
			<div className="flex items-center justify-between">
				<div>
					<div className="text-[13px] font-medium">Users</div>
					<div className="text-[11px] text-[var(--color-text-muted)]">
						{total} total · page {page} / {totalPages}
					</div>
				</div>
				<div className="flex items-center gap-1">
					<Pill
						onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
						disabled={offset === 0}
					>
						Prev
					</Pill>
					<Pill
						onClick={() => setOffset(offset + PAGE_SIZE)}
						disabled={offset + PAGE_SIZE >= total}
					>
						Next
					</Pill>
				</div>
			</div>

			<div className="space-y-1.5">
				{usersQ.isLoading && (
					<div className="text-[12px] text-[var(--color-text-muted)] py-4 text-center">
						loading…
					</div>
				)}
				{users.map((u) => (
					<UserRow
						key={u.id}
						user={u}
						plan={planByUserId.get(u.id) ?? null}
					/>
				))}
				{!usersQ.isLoading && users.length === 0 && (
					<div className="text-[12px] text-[var(--color-text-muted)] py-4 text-center">
						No users on this page.
					</div>
				)}
			</div>
		</GlassPanel>
	);
}

function UserRow({
	user,
	plan,
}: {
	user: AdminUser;
	plan: PlanRow | null;
}) {
	const qc = useQueryClient();
	const session = useSession();
	const navigate = useNavigate();
	const isSelf = session.data?.user?.id === user.id;

	const [banOpen, setBanOpen] = useState(false);
	const [removeOpen, setRemoveOpen] = useState(false);

	const impersonate = useMutation({
		mutationFn: async () => {
			const r = await authClient.admin.impersonateUser({ userId: user.id });
			if (r.error) throw new Error(r.error.message ?? "impersonate failed");
		},
		onSuccess: () => {
			toast.info(`Impersonating ${user.email}`);
			// Hard navigate so the new session takes effect everywhere.
			window.location.href = "/";
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't impersonate" }),
	});

	const ban = useMutation({
		mutationFn: async () => {
			const r = await authClient.admin.banUser({
				userId: user.id,
				banReason: "Banned by admin",
			});
			if (r.error) throw new Error(r.error.message ?? "ban failed");
		},
		onSuccess: () => {
			toast.success(`${user.email} banned`);
			qc.invalidateQueries({ queryKey: ["admin", "users"] });
		},
		onError: (e: Error) => toast.error(e.message, { title: "Ban failed" }),
	});

	const unban = useMutation({
		mutationFn: async () => {
			const r = await authClient.admin.unbanUser({ userId: user.id });
			if (r.error) throw new Error(r.error.message ?? "unban failed");
		},
		onSuccess: () => {
			toast.success(`${user.email} unbanned`);
			qc.invalidateQueries({ queryKey: ["admin", "users"] });
		},
		onError: (e: Error) => toast.error(e.message, { title: "Unban failed" }),
	});

	const setRole = useMutation({
		mutationFn: async (role: string) => {
			const r = await authClient.admin.setRole({ userId: user.id, role });
			if (r.error) throw new Error(r.error.message ?? "set-role failed");
			return role;
		},
		onSuccess: (role) => {
			toast.success(`${user.email} → ${role}`);
			qc.invalidateQueries({ queryKey: ["admin", "users"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Role change failed" }),
	});

	const remove = useMutation({
		mutationFn: async () => {
			const r = await authClient.admin.removeUser({ userId: user.id });
			if (r.error) throw new Error(r.error.message ?? "remove failed");
		},
		onSuccess: () => {
			toast.success(`${user.email} removed`);
			qc.invalidateQueries({ queryKey: ["admin", "users"] });
		},
		onError: (e: Error) => toast.error(e.message, { title: "Remove failed" }),
	});

	return (
		<div className="glass rounded-[10px] flex items-center gap-3 px-3 py-2.5">
			{user.image ? (
				// eslint-disable-next-line @next/next/no-img-element
				<img
					src={user.image}
					alt=""
					className="w-7 h-7 rounded-full shrink-0 opacity-90"
				/>
			) : (
				<div className="w-7 h-7 rounded-full bg-white/[0.06] border border-[var(--color-border)] shrink-0" />
			)}
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className="text-[13px] truncate">{user.name ?? "—"}</span>
					{user.role === "admin" && (
						<span className="text-[10px] uppercase tracking-wider text-[var(--color-text)] bg-white/[0.06] border border-[var(--color-border)] px-1.5 py-0.5 rounded">
							admin
						</span>
					)}
					{user.banned && (
						<span className="text-[10px] uppercase tracking-wider text-[var(--color-danger)] border border-[rgba(239,68,68,0.25)] px-1.5 py-0.5 rounded">
							banned
						</span>
					)}
					{isSelf && (
						<span className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
							you
						</span>
					)}
				</div>
				<div className="text-[11px] text-[var(--color-text-muted)] truncate flex items-center gap-2">
					<span className="mono">{user.email}</span>
					<span className="text-[var(--color-text-dim)]">·</span>
					<PlanChipPopover
						userId={user.id}
						email={user.email}
						planId={plan?.planId ?? "free"}
						locked={plan?.lockedByAdmin === true}
						notes={plan?.notes ?? null}
					/>
					<span className="text-[var(--color-text-dim)]">·</span>
					<span className="text-[var(--color-text-dim)]">
						{relativeTime(user.createdAt)}
					</span>
				</div>
			</div>
			<UserActionsMenu
				disabled={isSelf}
				banned={!!user.banned}
				isAdmin={user.role === "admin"}
				onImpersonate={() => impersonate.mutate()}
				onBan={() => setBanOpen(true)}
				onUnban={() => unban.mutate()}
				onMakeAdmin={() => setRole.mutate("admin")}
				onMakeUser={() => setRole.mutate("user")}
				onRemove={() => setRemoveOpen(true)}
			/>

			<ConfirmDialog
				open={banOpen}
				onOpenChange={setBanOpen}
				title="Ban this user?"
				body={
					<>
						Bans{" "}
						<span className="mono text-[var(--color-text)]">{user.email}</span>.
						Their active sessions remain valid until they expire — they just
						can't sign in again. Use the unban action to reverse.
					</>
				}
				confirmLabel="Ban"
				tone="danger"
				onConfirm={() => ban.mutateAsync()}
			/>
			<ConfirmDialog
				open={removeOpen}
				onOpenChange={setRemoveOpen}
				title="Remove this user?"
				body={
					<>
						Hard-deletes{" "}
						<span className="mono text-[var(--color-text)]">{user.email}</span>{" "}
						and cascades to all their projects, jobs, webhooks, and tenant data.
						No undo.
					</>
				}
				confirmText={user.email}
				confirmLabel="Remove user"
				tone="danger"
				onConfirm={() => remove.mutateAsync()}
			/>
		</div>
	);
}

interface UserActionsMenuProps {
	disabled?: boolean;
	banned: boolean;
	isAdmin: boolean;
	onImpersonate: () => void;
	onBan: () => void;
	onUnban: () => void;
	onMakeAdmin: () => void;
	onMakeUser: () => void;
	onRemove: () => void;
}

function UserActionsMenu({
	disabled,
	banned,
	isAdmin,
	onImpersonate,
	onBan,
	onUnban,
	onMakeAdmin,
	onMakeUser,
	onRemove,
}: UserActionsMenuProps) {
	return (
		<Menu.Root>
			<Menu.Trigger
				disabled={disabled}
				className="pill pill-icon !w-8 !h-8 text-[var(--color-text-dim)] hover:text-[var(--color-text)] disabled:opacity-40 disabled:cursor-not-allowed"
				aria-label="User actions"
			>
				<MoreHorizontal className="w-3.5 h-3.5" />
			</Menu.Trigger>
			<Menu.Portal>
				<Menu.Positioner sideOffset={6} align="end" className="z-50">
					<Menu.Popup
						className="rounded-[10px] py-1 min-w-[200px]"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
						}}
					>
						<Menu.Item
							onClick={onImpersonate}
							className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
						>
							<LogIn className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
							Impersonate
						</Menu.Item>
						{isAdmin ? (
							<Menu.Item
								onClick={onMakeUser}
								className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
							>
								<UserCheck className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
								Demote to user
							</Menu.Item>
						) : (
							<Menu.Item
								onClick={onMakeAdmin}
								className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
							>
								<Shield className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
								Make admin
							</Menu.Item>
						)}
						{banned ? (
							<Menu.Item
								onClick={onUnban}
								className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
							>
								<ShieldOff className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
								Unban
							</Menu.Item>
						) : (
							<Menu.Item
								onClick={onBan}
								className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06]"
							>
								<Ban className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
								Ban
							</Menu.Item>
						)}
						<div className="border-t border-[var(--color-border)] my-1 mx-1" />
						<Menu.Item
							onClick={onRemove}
							className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] text-[var(--color-danger)] outline-none cursor-pointer data-[highlighted]:bg-[rgba(239,68,68,0.08)]"
						>
							<Trash2 className="w-3.5 h-3.5" />
							Remove user
						</Menu.Item>
					</Menu.Popup>
				</Menu.Positioner>
			</Menu.Portal>
		</Menu.Root>
	);
}

// ---------------------------------------------------------------------------
// Invitations section

async function fetchInvitations(): Promise<Invitation[]> {
	const r = await fetch("/api/admin/invitations", { credentials: "include" });
	if (!r.ok) throw new Error(`invitations: ${r.status}`);
	const body = (await r.json()) as { invitations: Invitation[] };
	return body.invitations;
}

function InvitationsSection() {
	const qc = useQueryClient();
	const invitesQ = useQuery({
		queryKey: ["admin", "invitations"],
		queryFn: fetchInvitations,
	});

	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);

	const create = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/admin/invitations", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: email.trim().toLowerCase() }),
			});
			if (!r.ok) {
				const body = (await r.json().catch(() => ({}))) as { error?: string };
				throw new Error(body.error ?? `invite: ${r.status}`);
			}
		},
		onSuccess: () => {
			toast.success(`Invited ${email.trim().toLowerCase()}`);
			setEmail("");
			setError(null);
			qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
		},
		onError: (e: Error) => {
			setError(e.message);
			toast.error(e.message, { title: "Invite failed" });
		},
	});

	const revoke = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/admin/invitations/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`revoke: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Invitation revoked");
			qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
		},
		onError: (e: Error) => toast.error(e.message, { title: "Revoke failed" }),
	});

	const invitations = invitesQ.data ?? [];
	const pending = invitations.filter((i) => i.status === "pending");

	return (
		<GlassPanel className="p-4 space-y-4">
			<div>
				<div className="text-[13px] font-medium">Invitations</div>
				<div className="text-[11px] text-[var(--color-text-muted)]">
					{invitations.length} total · {pending.length} pending
				</div>
			</div>

			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (!email.trim()) return;
					create.mutate();
				}}
				className="flex items-center gap-2"
			>
				<input
					type="email"
					value={email}
					onChange={(ev) => {
						setEmail(ev.target.value);
						setError(null);
					}}
					placeholder="user@company.com"
					autoComplete="off"
					spellCheck={false}
					className="flex-1 h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] mono text-[13px] outline-none focus:border-[var(--color-border-hi)]"
				/>
				<Pill type="submit" disabled={!email.trim() || create.isPending}>
					{create.isPending ? (
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
					) : (
						<Plus className="w-3.5 h-3.5" />
					)}
					Invite
				</Pill>
			</form>
			{error && (
				<div className="text-[12px] text-[var(--color-danger)]">{error}</div>
			)}

			<div className="space-y-1.5">
				{invitations.length === 0 && !invitesQ.isLoading && (
					<div className="text-[12px] text-[var(--color-text-muted)] py-4 text-center">
						No invitations yet.
					</div>
				)}
				{invitations.map((inv) => (
					<div
						key={inv.id}
						className="glass rounded-[10px] flex items-center justify-between gap-2 px-3 py-2.5"
					>
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2">
								<span className="mono text-[13px] truncate">{inv.email}</span>
								<StatusChip status={inv.status} />
							</div>
							<div className="text-[11px] text-[var(--color-text-muted)] truncate">
								invited by {inv.inviterName ?? inv.inviterEmail ?? "—"} ·{" "}
								{relativeTime(inv.createdAt)}
								{inv.acceptedAt && (
									<> · accepted {relativeTime(inv.acceptedAt)}</>
								)}
							</div>
						</div>
						{inv.status === "pending" && (
							<Pill
								onClick={() => revoke.mutate(inv.id)}
								className="!text-[var(--color-danger)]"
							>
								<X className="w-3.5 h-3.5" />
								Revoke
							</Pill>
						)}
					</div>
				))}
			</div>
		</GlassPanel>
	);
}

function StatusChip({ status }: { status: string }) {
	const cls =
		status === "accepted"
			? "text-[var(--color-success)] border-[rgba(34,197,94,0.25)]"
			: status === "revoked"
				? "text-[var(--color-danger)] border-[rgba(239,68,68,0.25)]"
				: "text-[var(--color-text-dim)] border-[var(--color-border)]";
	return (
		<span
			className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
		>
			{status}
		</span>
	);
}

function relativeTime(iso: string): string {
	const d = new Date(iso);
	const diff = Date.now() - d.getTime();
	if (diff < 0) return "just now";
	const s = Math.round(diff / 1000);
	if (s < 60) return `${s}s ago`;
	const m = Math.round(s / 60);
	if (m < 60) return `${m}m ago`;
	const h = Math.round(m / 60);
	if (h < 24) return `${h}h ago`;
	const days = Math.round(h / 24);
	return `${days}d ago`;
}

// ────────────────────────────────────────────────────────────────────
// Plans section (spec 25). Per-user plan + Whop subscription view,
// manual override action, and a feed of recent webhook events.
// Server-side: /api/admin/plans + /api/admin/users/:userId/plan +
// /api/admin/whop/events. All admin-gated.
// ────────────────────────────────────────────────────────────────────

type RenketsuPlanId = "free" | "lite" | "pro";

interface PlanRow {
	userId: string;
	email: string;
	name: string | null;
	role: string | null;
	planId: RenketsuPlanId;
	lockedByAdmin: boolean;
	notes: string | null;
	periodEnd: string | null;
	subscription: {
		status: string;
		reason: string | null;
		membershipId: string;
		whopPlanId: string;
		periodEnd: string | null;
		updatedAt: string;
	} | null;
}

interface WhopEventRow {
	id: string;
	eventType: string;
	receivedAt: string;
	processedAt: string | null;
	error: string | null;
	retryCount: number;
	payload: unknown;
}

// Plan override is inline on each Users row: the plan chip is the
// trigger, click opens a small popover with plan picker + lock +
// notes. No separate Plans tab, no modal dialog.

const PLAN_CHIP_COLORS: Record<RenketsuPlanId, string> = {
	free: "text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-border-hi)]",
	lite: "text-[var(--color-text)] border-[rgba(125,211,252,0.45)] bg-[rgba(125,211,252,0.08)] hover:border-[rgba(125,211,252,0.65)]",
	pro: "text-[var(--color-text)] border-[rgba(167,139,250,0.35)] bg-[rgba(167,139,250,0.07)] hover:border-[rgba(167,139,250,0.55)]",
};

function PlanChipPopover({
	userId,
	email,
	planId,
	locked,
	notes,
}: {
	userId: string;
	email: string;
	planId: RenketsuPlanId;
	locked: boolean;
	notes: string | null;
}) {
	const qc = useQueryClient();
	const [open, setOpen] = useState(false);
	const [draftPlan, setDraftPlan] = useState<RenketsuPlanId>(planId);
	const [draftLocked, setDraftLocked] = useState(locked);
	const [draftNotes, setDraftNotes] = useState(notes ?? "");

	// When the popover opens, snap the draft state to the row's current
	// values. Without this, a previous Cancel would leave stale draft
	// values from the last open.
	useEffect(() => {
		if (!open) return;
		setDraftPlan(planId);
		setDraftLocked(locked);
		setDraftNotes(notes ?? "");
	}, [open, planId, locked, notes]);

	const dirty =
		draftPlan !== planId ||
		draftLocked !== locked ||
		(draftNotes.trim() || null) !== (notes ?? null);

	const save = useMutation({
		mutationFn: async () => {
			const r = await fetch(`/api/admin/users/${userId}/plan`, {
				method: "PATCH",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					plan_id: draftPlan,
					locked: draftLocked,
					notes: draftNotes.trim() || undefined,
				}),
			});
			if (!r.ok) throw new Error(`override: ${r.status}`);
		},
		onSuccess: () => {
			toast.success(`${email} → ${draftPlan}`);
			qc.invalidateQueries({ queryKey: ["admin", "plans"] });
			setOpen(false);
		},
		onError: (err) =>
			toast.error(`Override failed: ${(err as Error).message}`),
	});

	return (
		<Popover.Root open={open} onOpenChange={setOpen}>
			<Popover.Trigger
				aria-label={`Change plan for ${email}`}
				className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border outline-none transition-colors inline-flex items-center gap-1 cursor-pointer ${PLAN_CHIP_COLORS[planId]}`}
			>
				<span>{planId}</span>
				<ChevronDown className="w-2.5 h-2.5 opacity-60" />
				{locked && (
					<span
						className="text-[var(--color-text-dim)] ml-0.5"
						title="Locked: the Whop reconciler will not change this row"
					>
						🔒
					</span>
				)}
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Positioner sideOffset={6} align="start" className="z-50">
					<Popover.Popup
						className="rounded-[10px] p-3 w-[260px] space-y-3"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
						}}
					>
						<div>
							<div className="text-[11px] text-[var(--color-text-dim)] uppercase tracking-wider mb-1.5">
								Plan
							</div>
							<div className="flex gap-1.5">
								{(["free", "lite", "pro"] as RenketsuPlanId[]).map((p) => (
									<button
										key={p}
										type="button"
										onClick={() => setDraftPlan(p)}
										className={`h-7 px-3 rounded-md text-[12px] border transition-colors flex-1 ${
											draftPlan === p
												? "bg-[var(--color-text)] text-[var(--color-bg)] border-[var(--color-text)]"
												: "bg-transparent border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border-hi)]"
										}`}
									>
										{p}
									</button>
								))}
							</div>
						</div>

						<label className="flex items-start justify-between gap-2 cursor-pointer">
							<div className="min-w-0">
								<div className="text-[12px]">Lock against Whop sync</div>
								<div className="text-[10px] text-[var(--color-text-dim)] mt-0.5">
									Skip this user in the daily reconciler.
								</div>
							</div>
							<input
								type="checkbox"
								checked={draftLocked}
								onChange={(e) => setDraftLocked(e.target.checked)}
								className="accent-[var(--color-text)] mt-1 shrink-0"
							/>
						</label>

						<div>
							<div className="text-[11px] text-[var(--color-text-dim)] uppercase tracking-wider mb-1.5">
								Notes <span className="lowercase">(optional)</span>
							</div>
							<input
								type="text"
								value={draftNotes}
								onChange={(e) => setDraftNotes(e.target.value)}
								placeholder="reason for the override"
								className="w-full h-8 px-2.5 rounded-md bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[12px] hover:border-[var(--color-border-hi)] focus:border-[var(--color-border-hi)] outline-none"
							/>
						</div>

						<div className="flex justify-end gap-1.5 pt-1">
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="h-7 px-2.5 rounded-md text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04]"
							>
								Cancel
							</button>
							<Pill
								onClick={() => save.mutate()}
								disabled={!dirty || save.isPending}
							>
								{save.isPending ? (
									<Loader2 className="w-3.5 h-3.5 animate-spin" />
								) : (
									<Check className="w-3.5 h-3.5" />
								)}
								Apply
							</Pill>
						</div>
					</Popover.Popup>
				</Popover.Positioner>
			</Popover.Portal>
		</Popover.Root>
	);
}

function WhopEventsPanel() {
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const eventsQ = useQuery({
		queryKey: ["admin", "whop", "events"],
		queryFn: async (): Promise<WhopEventRow[]> => {
			const r = await fetch("/api/admin/whop/events", { credentials: "include" });
			if (!r.ok) throw new Error(`events: ${r.status}`);
			return ((await r.json()) as { rows: WhopEventRow[] }).rows;
		},
	});

	return (
		<GlassPanel className="p-4 space-y-3">
			<div>
				<div className="text-[13px] font-medium">Whop events</div>
				<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
					Last 100 webhook deliveries.
				</div>
			</div>

			{eventsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[12px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : eventsQ.error ? (
				<div className="text-[12px] text-[var(--color-danger)]">
					{(eventsQ.error as Error).message}
				</div>
			) : (eventsQ.data ?? []).length === 0 ? (
				<div className="text-[12px] text-[var(--color-text-muted)] py-3">
					No Whop deliveries yet.
				</div>
			) : (
				<div className="space-y-1">
					{(eventsQ.data ?? []).map((e) => (
						<div
							key={e.id}
							className="rounded-md border border-[var(--color-border)] bg-white/[0.02] overflow-hidden"
						>
							<button
								type="button"
								onClick={() =>
									setExpandedId(expandedId === e.id ? null : e.id)
								}
								className="w-full flex items-center gap-3 px-3 py-2 text-left text-[12px] hover:bg-white/[0.02]"
							>
								<EventStatusDot processed={!!e.processedAt} error={!!e.error} />
								<div className="flex-1 min-w-0">
									<div className="flex items-baseline gap-2">
										<span className="font-medium">{e.eventType}</span>
										<span className="text-[10px] mono text-[var(--color-text-dim)] truncate">
											{e.id}
										</span>
									</div>
									<div className="text-[10px] text-[var(--color-text-dim)] mt-0.5">
										{relativeTime(e.receivedAt)}
										{e.retryCount > 0 && (
											<span className="ml-2">retries: {e.retryCount}</span>
										)}
									</div>
								</div>
							</button>
							{expandedId === e.id && (
								<div className="border-t border-[var(--color-border)] px-3 py-2 bg-black/20 space-y-2">
									{e.error && (
										<div className="text-[11px] text-[var(--color-danger)] mono">
											{e.error}
										</div>
									)}
									<pre className="mono text-[10px] text-[var(--color-text-dim)] whitespace-pre-wrap break-words bg-black/30 rounded-md p-2 border border-[var(--color-border)] max-h-[220px] overflow-y-auto">
										{JSON.stringify(e.payload, null, 2)}
									</pre>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</GlassPanel>
	);
}

function EventStatusDot({
	processed,
	error,
}: {
	processed: boolean;
	error: boolean;
}) {
	const color = error
		? "bg-[var(--color-danger)]"
		: processed
			? "bg-[var(--color-success)]"
			: "bg-[var(--color-text-dim)]";
	return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${color}`} />;
}

